import { useModuleStore } from "@/stores/moduleStore";
import { defineStore } from "pinia";
import dayjs from "../plugins/dayjs";

export const useStepsStore = defineStore("steps", () => {
  const moduleStore = useModuleStore();
  let authStore;
  let i18n;
  let DEVICE_ID = ref(null);
  let started = ref(false);
  const serverTime = ref(null);
  const lastStepUpdate = ref(localStorage.getItem("lastStepUpdate") ? dayjs(JSON.parse(localStorage.getItem("lastStepUpdate"))).toDate() : null);
  // Le podomètre natif ne pousse une valeur que lorsque le nombre de pas change : au lancement de l'app un
  // matin avant le premier pas, restaurer telle quelle la valeur persistée afficherait le total de la veille.
  const todaySteps = ref(
    lastStepUpdate.value && dayjs().isSame(lastStepUpdate.value, "day") ? JSON.parse(localStorage.getItem("todaySteps") || "0") : 0
  );
  const lastPedometerError = ref(null);

  /**
   * @param {import("vue-i18n").I18n} _i18n
   */
  function init(_i18n, _authStore) {
    i18n = _i18n;
    authStore = _authStore;
    // reattach pedometer on boot if enabled
    document.addEventListener("deviceready", async () => {
      DEVICE_ID.value =
        window.device ? `${window.device.uuid}:${window.device.version}:${window.device.manufacturer} ${window.device.model}`.substring(0, 60) : null;
      if (moduleStore.isPedometerEnabled) {
        refreshTodaySteps();
        await attachListener();
        await initBackgroundFetch();
      } else if (localStorage.getItem("pendingPedometerAuthorization")) {
        await enable();
      }
    });

    document.addEventListener("resume", async () => {
      if (moduleStore.isPedometerEnabled) {
        await refreshTodaySteps();
        if (started.value) {
          setTimeout(pushSteps);
        }
      }
    });
  }

  function setTodaySteps(steps) {
    todaySteps.value = steps;
    const today = dayjs();
    lastStepUpdate.value = today.toDate();
    localStorage.setItem("todaySteps", JSON.stringify(todaySteps.value));
    localStorage.setItem("lastStepUpdate", JSON.stringify(today.format()));
  }

  async function refreshTodaySteps() {
    if (!moduleStore.isPedometerEnabled || !window["stepper"]) return;
    try {
      const result = await window["stepper"].getStepsByPeriod(dayjs().startOf("day").toDate(), dayjs().endOf("day").toDate());
      setTodaySteps(Math.max(0, result.steps));
    } catch (error) {
      console.error("Failed to refresh today steps", error);
    }
  }

  let interval;
  function incrementServerTime() {
    const newTime = dayjs();
    if (!newTime.isSame(serverTime.value, "day")) {
      todaySteps.value = 0;
      localStorage.setItem("todaySteps", "0");
      refreshTodaySteps();
    }
    serverTime.value = newTime.toDate();
  }

  async function loadServerTime() {
    if (interval) clearInterval(interval);
    interval = setInterval(incrementServerTime, 30000);
    serverTime.value = dayjs().toDate();
  }

  function isAvailable() {
    return new Promise((resolve) => {
      if (!window.cordova?.platformId) return resolve(false);
      document.addEventListener("deviceready", () => {
        if (!window["stepper"]) resolve(false);
        else {
          let done = false;
          setTimeout(() => {
            if (!done) {
              resolve(true);
              done = true;
            }
          }, 4000);
          window["stepper"].isStepCountingAvailable(
            (result) => {
              if (!done) {
                done = true;
                resolve(result !== false);
              }
            },
            (error) => {
              console.error("Failed to check Pedometer availability", error);
              if (!done) {
                done = true;
                resolve(false);
              }
            }
          );
        }
      });
    });
  }

  async function pushSteps() {
    if (navigator.onLine === false) {
      console.log("offline");
      return;
    }
    // TODO: debounce and queue for offline
    const lastPushTime = parseInt(localStorage.getItem("lastPushTime")) || 0;
    const now = new Date().getTime();
    if (authStore.me && lastPushTime < now - 10 * 1000 && !/(00:0[0-2])|23:59/.test(dayjs().format("HH:mm"))) {
      // Updating lastPushTime to lock push
      localStorage.setItem("lastPushTime", now.toString());

      // Resolve dates to push
      const today = dayjs().format("YYYY-MM-DD");
      /*
       * TODO : if event is started days ago, try to get history steps from challenge start day
       * i.e. set lastSuccessPushDate to event start date
       */
      let lastSuccessPushDate = localStorage.getItem("lastSuccessPushDate") || today;
      const fiveDaysAgo = dayjs().add(-5, "day").format("YYYY-MM-DD");
      if (lastSuccessPushDate < fiveDaysAgo) lastSuccessPushDate = fiveDaysAgo;
      console.log("[pushSteps] Starts", lastSuccessPushDate, today);
      try {
        while (lastSuccessPushDate <= today) {
          const result = await window["stepper"].getStepsByPeriod(
            dayjs(lastSuccessPushDate).startOf("day").toDate(),
            dayjs(lastSuccessPushDate).endOf("day").toDate()
          );
          if (new Date().getTime() - now > 120000) {
            console.error("Took more than 2min to get steps");
            break;
          }
          console.log("[pushSteps] Got steps from stepper", lastSuccessPushDate, result);
          await sendPostRequest(`/n/api/event/${authStore.me.event}/steps`, {
            _stamp: now,
            day: lastSuccessPushDate,
            count: result.steps,
            manual: false,
            device_id: DEVICE_ID.value,
          });
          if (lastSuccessPushDate >= today) {
            break;
          }
          lastSuccessPushDate = dayjs(lastSuccessPushDate).add(1, "day").format("YYYY-MM-DD");
        }
      } catch (error) {
        console.log("Failed to push steps", error);
      }
      localStorage.setItem("lastSuccessPushDate", lastSuccessPushDate);
    }
  }

  async function initBackgroundFetch() {
    console.log("[BackgroundFetch] Init");
    if (window.cordova?.platformId === "android") {
      // set params for headless task
      if (authStore.auth) {
        window["NativeStorage"].setItem("headless_task_settings", {
          api_endpoint: `${localStorage.getItem("baseUrl") || import.meta.env.VITE_VUE_APP_API_BASE_PATH}n/api/event/${authStore.me.event}/steps`,
          access_token: authStore.auth.access_token,
          x_device: `${window.device.version}:${window.device.manufacturer} ${window.device.model}`,
          device_id: DEVICE_ID.value,
          release: import.meta.env.VITE_VUE_APP_RELEASE || "alvarum-go-" + (window.cordova?.platformId || "web") + "@dev",
          time_zone: authStore.me.event_event.timezone,
        });
      }
    }
    let status;
    try {
      status = await window["BackgroundFetch"].configure(
        {
          minimumFetchInterval: 15,
          stopOnTerminate: false,
          startOnBoot: true,
          enableHeadless: true,
          requiredNetworkType: window["BackgroundFetch"].NETWORK_TYPE_ANY,
        },
        async (taskId) => {
          console.log("[BackgroundFetch] Starting", taskId);
          // TODO: fetch steps from db
          await pushSteps();
          console.log("[BackgroundFetch] Completing", taskId);
          window["BackgroundFetch"].finish(taskId);
        },
        async (taskId) => {
          console.error("[BackgroundFetch] TIMEOUT: ", taskId);
          window["BackgroundFetch"].finish(taskId);
        }
      );
    } catch (e) {
      // configure() rejette (au lieu de résoudre) le status quand != STATUS_AVAILABLE
      // -> origine des "Non-Error promise rejection captured with value: 1/0" dans Sentry
      status = e;
    }

    const statusLabel = { 0: "RESTRICTED", 1: "DENIED", 2: "AVAILABLE" }[status] ?? "UNKNOWN";
    if (status !== window["BackgroundFetch"].STATUS_AVAILABLE) {
      console.error("[BackgroundFetch] unavailable", statusLabel, status, window.cordova?.platformId);
    } else {
      console.log("[BackgroundFetch] STARTS: ", status);
    }
  }

  function requestPermissions() {
    return new Promise((resolve) => {
      if (!window["stepper"]) return;
      if (window.cordova?.platformId === "ios") {
        resolve(true);
        return;
      }
      window["stepper"].requestPermission(
        // POST_NOTIFICATIONS is a single app-wide permission, already prompted for by OneSignal in
        // notificationStore. A second dialog here looks identical but means something else to the user, and since
        // Android permanently denies the permission after two refusals it would also burn the app's last chance to
        // ever ask again. Step counting works without it; only the progress notification stays hidden.
        { skipNotificationPermission: true },
        async (status) => {
          if (status === false) {
            resolve(false);
            return;
          }
          await ensureBackgroundExecution();
          resolve(true);
        },
        (error) => {
          console.error("Permission refused", error);
          resolve(false);
        }
      );
    });
  }

  async function ensureBackgroundExecution() {
    if (window.cordova?.platformId !== "android") return;
    try {
      // On MIUI/HyperOS the AOSP battery dialog is hijacked into an obscure multi-mode picker and the Doze
      // whitelist alone does not stop the OS from freezing background sync. Those devices need the vendor
      // Autostart + battery screens, gated behind a user prompt owned by the calling component (a confirm
      // opened from a Pinia store has no Vue app context, so Vuetify can't resolve its defaults instance).
      if (await window["stepper"].isVendorBatteryRestricted()) return;
      await window["stepper"].disableBatteryOptimizations();
    } catch (error) {
      console.error("Failed to request background execution", error);
    }
  }

  async function getVendorBackgroundStatus() {
    try {
      return await window["stepper"].getVendorBackgroundStatus();
    } catch (error) {
      console.error("Failed to check vendor background status", error);
      return { vendor: false };
    }
  }

  async function needsVendorBackgroundPrompt() {
    if (window.cordova?.platformId !== "android") return false;
    const status = await getVendorBackgroundStatus();
    if (!status.vendor || (status.autostartAllowed === true && status.batteryUnrestricted === true)) return false;
    return status;
  }

  function openVendorBatteryMode() {
    return window["stepper"].openVendorBatterySettings();
  }

  function openVendorAutostart() {
    return window["stepper"].requestVendorAutostart();
  }

  function openAppSettings() {
    // iOS tue l'app quand une autorisation de confidentialité change.
    localStorage.setItem("pendingPedometerAuthorization", "1");
    window.open("app-settings:", "_system");
  }

  function needsPedometerAuthorizationPrompt() {
    // CMErrorMotionActivityNotAuthorized : « Mouvement et forme » refusé pour l'app, ou désactivé pour tout
    // le téléphone. iOS ne réaffiche jamais son prompt une fois refusé, seul un passage par les Réglages
    // débloque.
    const CM_ERROR_NOT_AUTHORIZED = 105;
    return window.cordova?.platformId === "ios" && lastPedometerError.value === CM_ERROR_NOT_AUTHORIZED;
  }

  function enable() {
    return new Promise((resolve, reject) => {
      if (!window["stepper"]) reject("unsupported platform");
      localStorage.removeItem("pendingPedometerAuthorization");
      moduleStore.setPedometerEnabled(true);
      let done = false;
      requestPermissions().then((granted) => {
        if (granted === false) {
          done = true;
          moduleStore.setPedometerEnabled(false);
          resolve(false);
          return;
        }
        attachListener().then((result) => {
          if (!done) {
            done = true;
            moduleStore.setPedometerEnabled(!!result);
            if (result) {
              setTimeout(() => initBackgroundFetch());
            }
            resolve(!!result);
          }
        });
        setTimeout(() => {
          if (!done) {
            done = true;
            setTimeout(() => initBackgroundFetch());
            resolve(true);
          }
        }, 3000);
      });
    });
  }

  function attachListener() {
    return new Promise((resolve) => {
      let done = false;
      lastPedometerError.value = null;
      window["stepper"].startStepperUpdates(
        {
          // Must stay in sync with requestPermissions: the plugin asks for POST_NOTIFICATIONS from both entry
          // points, so leaving it out here brings the dialog back.
          skipNotificationPermission: true,
          pedometerIsCountingText: i18n.global.t("pedometer.notification.title"),
          pedometerStepsToGoFormatText: i18n.global.t("pedometer.notification.no_step"),
          pedometerYourProgressFormatText: i18n.global.t("pedometer.notification.no_step"),
          pedometerGoalReachedFormatText: i18n.global.t("pedometer.notification.steps", ["%s"]),
          timeZone: authStore.me.event_event.timezone,
        },
        (result) => {
          started.value = true;
          if (result.steps_today < 0) result.steps_today = 0;
          setTodaySteps(result.steps_today);
          if (!done) {
            done = true;
            resolve(true);
          }
          setTimeout(() => pushSteps());
        },
        (error) => {
          const code = Number(/CMErrorDomain\D+(\d+)/.exec(String(error))?.[1]) || null;
          lastPedometerError.value = code;
          console.error("Pedometer error", code ? `CMErrorDomain ${code}` : error?.message || error);
          if (!done) {
            done = true;
            resolve(false);
          }
        }
      );
    });
  }

  function disable() {
    return new Promise((resolve, reject) => {
      if (!window["stepper"]) reject("unsupported platform");
      window["stepper"].stopStepperUpdates(
        () => {
          moduleStore.setPedometerEnabled(false);
          window["BackgroundFetch"].stop(
            (taskId) => {
              console.log("[BackgroundFetch] Stoped", taskId);
            },
            (error) => {
              console.log("[BackgroundFetch] Failed to stop", error);
            }
          );
          resolve(true);
        },
        (error) => {
          console.error("Failed to stop pedometer", error);
          resolve(false);
        }
      );
    });
  }

  function sendPostRequest(url, data) {
    const baseUrl =
      localStorage.getItem("baseUrl") ||
      import.meta.env.VITE_VUE_APP_API_BASE_PATH ||
      (window.cordova?.platformId ? "https://go.preprod.alvarum.com/" : window.location.origin + "/");
    // Use Http client which works in background
    return new Promise((resolve, reject) => {
      if (!window.cordova["plugin"] || !window.cordova["plugin"].http) reject("HTTP client not loaded");

      window.cordova["plugin"].http.sendRequest(
        baseUrl + url.replace(/^\//, ""),
        {
          method: "post",
          data,
          serializer: "json",
          headers: {
            "Content-Type": "application/json",
            "X-App-Version": import.meta.env.VITE_VUE_APP_RELEASE || "alvarum-go-" + (window.cordova?.platformId || "web") + "@dev",
            ...(window.device && { "X-Device": `${window.device.version}:${window.device.manufacturer} ${window.device.model}` }),
            ...(authStore.auth && { Authorization: `Bearer ${authStore.auth.access_token}` }),
          },
        },
        (response) => {
          if (response.status == 200 || response.status == 201) resolve();
          else reject("Request failed with status " + response.status + " " + response.data);
        },
        (response) => reject("Request failed: " + response.error)
      );
    });
  }

  return {
    todaySteps,
    setTodaySteps,
    refreshTodaySteps,
    lastStepUpdate,
    serverTime,
    incrementServerTime,
    loadServerTime,
    isAvailable,
    pushSteps,
    initBackgroundFetch,
    requestPermissions,
    enable,
    needsVendorBackgroundPrompt,
    getVendorBackgroundStatus,
    openVendorBatteryMode,
    openVendorAutostart,
    openAppSettings,
    needsPedometerAuthorizationPrompt,
    lastPedometerError,
    attachListener,
    disable,
    init,
  };
});
